import { Injectable, signal } from '@angular/core';

export interface ActAsOrg {
  id: number;
  name: string;
  /** The org's URL base is /o/<slug>, acting-as included. */
  slug: string;
}

const STORE_KEY = 'actAsOrg';

/** The super admin's act-as switch (org plan, impersonation): while an
 *  org is set here, the org-headers interceptor sends `X-Org-Context`
 *  on every API request, the backend scopes the request to that org and
 *  audits it, and the layout shows the persistent banner. Session-
 *  scoped storage on purpose: an act-as must not quietly survive into
 *  tomorrow's browser session. */
@Injectable({ providedIn: 'root' })
export class ActAsService {

  readonly org = signal<ActAsOrg | null>(restore());

  start(org: ActAsOrg): void {
    this.org.set(org);
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(org));
    } catch {
      // Storage full/blocked: the signal still carries this tab.
    }
  }

  stop(): void {
    this.org.set(null);
    try {
      sessionStorage.removeItem(STORE_KEY);
    } catch {
      // Nothing stored, nothing to lose.
    }
  }
}

function restore(): ActAsOrg | null {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.id === 'number'
      && typeof parsed?.name === 'string'
      && typeof parsed?.slug === 'string'
      ? { id: parsed.id, name: parsed.name, slug: parsed.slug }
      : null;
  } catch {
    return null;
  }
}
